import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Dialog title of a step. The step moves focus to it when it has no field of its own (`data-autofocus`),
 * so that the new step is announced from its heading.
 */
export function StepTitle({
  children,
  size = 'h3',
  focusTarget = false,
}: {
  children: ReactNode;
  size?: 'h2' | 'h3';
  focusTarget?: boolean;
}) {
  return (
    <Dialog.Title asChild>
      <h2
        tabIndex={focusTarget ? -1 : undefined}
        data-autofocus={focusTarget ? '' : undefined}
        className={cn(
          'text-center text-[#0F1012] focus:outline-none',
          size === 'h2' ? 'dlz-h2' : 'dlz-h3',
        )}
      >
        {children}
      </h2>
    </Dialog.Title>
  );
}

export function StepDescription({ children }: { children: ReactNode }) {
  return (
    <Dialog.Description className="dlz-body-m text-center text-[#4F545C]">
      {children}
    </Dialog.Description>
  );
}

/** Figma „Heading“ of the steps 1–3: round 64 px icon badge, title and description, 24 / 8 px apart. */
export function StepHeading({
  icon,
  iconSize,
  title,
  description,
  focusTitle = false,
}: {
  icon: IconName;
  iconSize: number;
  title: string;
  description: string;
  focusTitle?: boolean;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-6">
      <span className="flex size-16 items-center justify-center rounded-full bg-[#F5F6F7] text-[#D7191F]">
        <Icon name={icon} size={iconSize} />
      </span>
      <div className="flex w-full flex-col gap-2">
        <StepTitle focusTarget={focusTitle}>{title}</StepTitle>
        <StepDescription>{description}</StepDescription>
      </div>
    </div>
  );
}
